import { type DragEvent, useCallback, useRef, useState } from "react";

type UseFileDropZoneOptions = {
  acceptFile?: (file: File) => boolean;
  disabled?: boolean;
  invalidDropMessage: string;
  onFilesDrop: (files: File[]) => void;
};

const hasDraggedFiles = (event: DragEvent<HTMLElement>): boolean =>
  Array.from(event.dataTransfer.types).includes("Files");

/** Widened past File so upload preflight can reuse it on plain metadata. */
export const isPdfFile = (file: Pick<File, "name" | "type">): boolean =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

export const useFileDropZone = ({
  acceptFile = () => true,
  disabled = false,
  invalidDropMessage,
  onFilesDrop,
}: UseFileDropZoneOptions) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  const dragDepthRef = useRef(0);

  const resetDragState = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDragActive(false);
  }, []);

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!hasDraggedFiles(event)) {
        return;
      }

      event.preventDefault();
      dragDepthRef.current += 1;

      if (!disabled) {
        setIsDragActive(true);
        setDropError(null);
      }
    },
    [disabled],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!hasDraggedFiles(event)) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = disabled ? "none" : "copy";
    },
    [disabled],
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (!hasDraggedFiles(event)) {
      return;
    }

    dragDepthRef.current = Math.max(dragDepthRef.current - 1, 0);

    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!hasDraggedFiles(event)) {
        return;
      }

      event.preventDefault();
      resetDragState();

      if (disabled) {
        return;
      }

      const files = Array.from(event.dataTransfer.files).filter(acceptFile);

      if (files.length === 0) {
        setDropError(invalidDropMessage);

        return;
      }

      setDropError(null);
      onFilesDrop(files);
    },
    [acceptFile, disabled, invalidDropMessage, onFilesDrop, resetDragState],
  );

  return {
    dropError,
    dropZoneProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
    isDragActive,
    resetDropError: () => setDropError(null),
    setDropError,
  };
};
